import React from "react";
import {
  AppBar,
  Box,
  Chip,
  Divider,
  IconButton,
  Toolbar,
  Typography,
} from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link as RouterLink } from "react-router-dom";
import thryivLogo from "../assets/Thryiv_logo_tm_TB.png";
import CompanyLogo from "./CompanyLogo";
import { LiveClock } from "./KioskStepBar";
import { useEmployerCompany } from "../hooks/useEmployerCompany";
import { BRAND } from "../styles/authTheme";

const iconButtonSx = {
  color: BRAND.navy,
  bgcolor: BRAND.bg,
  border: `1px solid ${BRAND.border}`,
  "&:hover": { bgcolor: "#EEF2F7" },
};

const StatusChips = ({ online, pendingCount, size = "small", compact = false }) => (
  <>
    {!online && (
      <Chip
        label={compact ? "Off" : "Offline"}
        size={size}
        sx={{
          bgcolor: "#FEF3C7",
          color: "#92400E",
          fontWeight: 700,
          fontSize: compact ? "0.6rem" : "0.68rem",
          height: compact ? 20 : 24,
          "& .MuiChip-label": { px: compact ? 0.75 : 1 },
        }}
      />
    )}
    {pendingCount > 0 && (
      <Chip
        label={compact ? pendingCount : `${pendingCount} pending`}
        size={size}
        sx={{
          bgcolor: BRAND.orangeLight,
          color: BRAND.orange,
          fontWeight: 700,
          fontSize: compact ? "0.6rem" : "0.68rem",
          height: compact ? 20 : 24,
          border: "1px solid rgba(230,90,0,0.25)",
          "& .MuiChip-label": { px: compact ? 0.75 : 1 },
        }}
      />
    )}
  </>
);

const KioskHeader = ({
  title = "Attendance Kiosk",
  subtitle = "Worker check-in / check-out",
  showBack = false,
  backTo = "/punch",
  onBack,
  online = true,
  pendingCount = 0,
  showSettings = true,
}) => {
  const { companyName, companyLogo, loading } = useEmployerCompany();
  const displayName = companyName || "Your organization";
  const hasStatus = !online || pendingCount > 0;

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        bgcolor: "#fff",
        color: BRAND.navy,
        borderBottom: `1px solid ${BRAND.border}`,
        boxShadow: "0 1px 8px rgba(15, 23, 42, 0.06)",
        pt: "env(safe-area-inset-top, 0px)",
      }}
    >
      {/* Mobile layout (xs) — single compact row */}
      <Box
        sx={{
          display: { xs: "flex", sm: "none" },
          alignItems: "center",
          gap: 0.75,
          px: 1.5,
          py: 0.75,
          minHeight: 48,
        }}
      >
        {showBack && (
          <IconButton
            component={onBack ? "button" : RouterLink}
            to={onBack ? undefined : backTo}
            onClick={onBack}
            aria-label="Go back"
            size="small"
            sx={{ ...iconButtonSx, flexShrink: 0, width: 32, height: 32 }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}

        <CompanyLogo
          logoPath={companyLogo}
          companyName={displayName}
          height={28}
          fallbackSrc={thryivLogo}
          alt={`${displayName} logo`}
        />

        <Typography
          noWrap
          sx={{
            flex: 1,
            minWidth: 0,
            fontWeight: 800,
            fontSize: "0.82rem",
            color: BRAND.navy,
            lineHeight: 1,
          }}
        >
          {title}
        </Typography>

        {hasStatus && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
            <StatusChips online={online} pendingCount={pendingCount} size="small" compact />
          </Box>
        )}

        <LiveClock variant="light" inline timeOnly />

        {showSettings && (
          <IconButton
            component={RouterLink}
            to="/settings"
            aria-label="Settings"
            size="small"
            sx={{ ...iconButtonSx, flexShrink: 0, width: 32, height: 32 }}
          >
            <SettingsOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>

      {/* Desktop layout (sm+) */}
      <Toolbar
        sx={{
          display: { xs: "none", sm: "flex" },
          minHeight: 72,
          gap: 2,
          px: 3,
        }}
      >
        {showBack && (
          <IconButton
            component={onBack ? "button" : RouterLink}
            to={onBack ? undefined : backTo}
            onClick={onBack}
            aria-label="Go back"
            sx={iconButtonSx}
          >
            <ArrowBackIcon />
          </IconButton>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.75,
            flexGrow: 1,
            minWidth: 0,
          }}
        >
          <CompanyLogo
            logoPath={companyLogo}
            companyName={displayName}
            height={48}
            fallbackSrc={thryivLogo}
            alt={`${displayName} logo`}
          />

          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              noWrap
              sx={{
                fontWeight: 800,
                fontSize: "1.05rem",
                color: BRAND.navy,
                lineHeight: 1.2,
              }}
            >
              {loading ? "Loading…" : displayName}
            </Typography>

            <Typography
              variant="body2"
              noWrap
              sx={{
                color: BRAND.muted,
                fontWeight: 600,
                fontSize: "0.82rem",
                lineHeight: 1.3,
              }}
            >
              {title}
            </Typography>

            {subtitle && (
              <Typography
                variant="caption"
                sx={{
                  color: BRAND.muted,
                  display: "block",
                  mt: 0.15,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          <LiveClock variant="light" />
          <Divider orientation="vertical" flexItem sx={{ borderColor: BRAND.border }} />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
          <StatusChips online={online} pendingCount={pendingCount} />
          {showSettings && (
            <IconButton
              component={RouterLink}
              to="/settings"
              aria-label="Settings"
              sx={iconButtonSx}
            >
              <SettingsOutlinedIcon />
            </IconButton>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default KioskHeader;
